import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import totusLogo from 'common/images/logo.svg';
import { mdiAccount, mdiBell, mdiBellOutline, mdiCogOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import { Button, IconButton as IButton, Tooltip } from '@mollybet/ui';
import useSelector from 'common/hooks/useSelector';
import SettingsDropdown from './components/settingsDropdown';
import calculatePermissions from 'common/utils/calculatePermissions';
import NotificationsDropdown from './components/notificationsDropdown';
import { RuntimeConfig } from 'common/state/config/actions';
import {
  isCloneDb,
  isDangerousDev,
  isProductionEnvironment,
} from 'common/utils/dev';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { NOTIFICATION_REFRESH_RATE } from 'common/configs/notifications';
import { notificationsRecipe } from 'common/state/notifications/recipes';
import { defaultNotificationsState } from 'common/state/notifications/states';
import { doGetNotifications } from 'common/state/notifications/thunkActions';
import {
  Notification,
  NotificationSatuses,
} from 'common/state/notifications/actions';
import { useTheme } from '@mollybet/ui/dist/styled';
import { api } from 'api';
import {
  acceptTermsAndConditionsRecipe,
  termsAndConditionsRecipe,
} from 'common/state/customers/recipes';
import {
  defaultAcceptTermsAndConditionsState,
  defaultTermsAndConditionsState,
} from 'common/state/customers/states';
import {
  doAcceptTermsAndConditions,
  doGetTermsAndConditions,
} from 'common/state/customers/thunkActions';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  background-color: ${(props) => props.theme.menu.background};
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 2px -2px rgba(0, 0, 0, 0.25);
  padding: 0px 10px;
  z-index: 99;
`;

const NavSpacer = styled.div`
  width: 100%;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 2rem;
`;

const LinkContainer = styled.div`
  display: flex;
  margin-left: 50px;
`;

const Ul = styled.ul`
  align-self: flex-end;
  margin: 0;
`;

const Li = styled.li`
  display: inline-block;
`;

const StyledNavLink = styled(NavLink)`
  position: relative;
  display: block;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 450;
  color: ${(props) => props.theme.menu.foreground};
  padding: 1rem 1.5rem;
  transition: all 0.3s ease 0s;
  height: 100%;

  &.active {
    color: ${(props) => props.theme.primary};
    position: relative;

    &::after {
      position: absolute;
      display: block;
      bottom: 0;
      left: 0;
      content: ' ';
      height: 0.25rem;
      width: 100%;
      border-radius: 0.25rem 0.25rem 0 0;
      background-color: ${(props) => props.theme.primary};
    }
  }

  &:hover {
    color: ${(props) => props.theme.primary};
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.menu.foreground};
`;

const UsernameContainer = styled.div`
  font-size: 14pt;
  color: ${(props) => props.theme.menu.foreground};
  margin: 0px 20px 0px 10px;
`;

const IconButton = styled(IButton)`
  color: ${(props) => props.theme.menu.foreground};
`;

const NotificationIndication = styled.div`
  position: absolute;
  background: ${({ theme }) => theme.danger};
  font-size: 0.75rem;
  font-weight: bold;
  padding: 0.25rem;
  height: 1.25rem;
  border-radius: 0.675rem;
  top: 1.75rem;
  left: 2rem;
  box-sizing: border-box;
`;

const ConfigTableElement = styled.table`
  background: ${(props) => props.theme.menu.background};

  tr td {
    padding: 0.25rem;

    &:first-child {
      text-transform: uppercase;
    }
    &:nth-child(2) {
      font-weight: bold;
    }
  }
`;

const includesAny = (array1: Array<unknown>, array2: Array<unknown>) =>
  array1.some((i) => array2.includes(i));

const WarningMessage = styled.div`
  background-color: ${(props) => props.theme.danger};
  font-weight: bold;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;
`;
const OkMessage = styled.div`
  background-color: ${(props) => props.theme.primary};
  font-weight: bold;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;
`;
const TermsMessage = styled.div`
  background-color: ${(props) => props.theme.warning};
  font-weight: bold;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 1rem;

  a {
    color: inherit;
    margin: 0 1rem 0 0;
  }
`;

const ConfigTable = ({ config }: { config: RuntimeConfig }) => {
  return (
    <ConfigTableElement>
      <tbody>
        <tr>
          <td>Environment:</td>
          <td>{config.deployment.environment}</td>
        </tr>
        <tr>
          <td>Database:</td>
          <td>{config.deployment.database}</td>
        </tr>
      </tbody>
    </ConfigTableElement>
  );
};

function NavBar() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navRef = useRef<HTMLElement>();
  const lastNotificationUpdate = useRef<number>(0);
  const theme = useTheme();

  const [notificationsState, notificationsDispatch] = useProducerWithThunks(
    notificationsRecipe,
    defaultNotificationsState
  );
  const [termsState, termsDispatch] = useProducerWithThunks(
    termsAndConditionsRecipe,
    defaultTermsAndConditionsState
  );
  const [, acceptTermsDispatch] = useProducerWithThunks(
    acceptTermsAndConditionsRecipe,
    defaultAcceptTermsAndConditionsState
  );

  const acceptTermsAndConditions = useCallback(async () => {
    // eslint-disable-next-line
    // @ts-ignore
    await acceptTermsDispatch(doAcceptTermsAndConditions());
    termsDispatch(doGetTermsAndConditions());
  }, [termsDispatch, acceptTermsDispatch]);

  const getNotifications = useCallback(() => {
    notificationsDispatch(doGetNotifications());
    lastNotificationUpdate.current = +new Date();
  }, [notificationsDispatch]);
  const throttledGetNotifications = useCallback(() => {
    if (
      +new Date() - lastNotificationUpdate.current >
      NOTIFICATION_REFRESH_RATE
    ) {
      getNotifications();
    }
  }, [getNotifications]);

  useEffect(() => {
    termsDispatch(doGetTermsAndConditions());
  }, [termsDispatch]);

  useEffect(() => {
    throttledGetNotifications();

    const intervalTask = setInterval(
      throttledGetNotifications,
      NOTIFICATION_REFRESH_RATE / 10
    );

    return () => clearInterval(intervalTask);
  }, [throttledGetNotifications, notificationsDispatch]);

  const { username, groups } = useSelector((state) => state.auth.user);
  // eslint-disable-next-line
  // @ts-ignore
  const { impersonator } = useSelector((state) => state.auth.user);

  const permissions = useMemo(() => calculatePermissions(groups), [groups]);
  const [newNotifications, ackedNotifications] = useMemo(() => {
    const newNotifications: Notification[] = [];
    const ackedNotifications: Notification[] = [];

    notificationsState.notifications?.forEach((notification) => {
      if (notification.status === NotificationSatuses.NEW)
        newNotifications.push(notification);
      if (notification.status === NotificationSatuses.ACKNOWLEDGED)
        ackedNotifications.push(notification);
    });

    return [newNotifications, ackedNotifications];
  }, [notificationsState]);

  const { config } = useSelector((state) => state.config);

  const unsudo = async () => {
    impersonator && api.post('/contact/unswitch/');
  };

  return (
    <>
      <Nav ref={navRef}>
        <Container>
          {isProductionEnvironment() ? (
            <LogoImage src={totusLogo} />
          ) : (
            <Tooltip content={<ConfigTable config={config} />}>
              <LogoImage src={totusLogo} />
            </Tooltip>
          )}
          <LinkContainer>
            <Ul>
              {includesAny(permissions, ['agentHome', 'customerHome']) && (
                <Li>
                  <StyledNavLink to="/home">Home</StyledNavLink>
                </Li>
              )}
              {includesAny(permissions, [
                'agentBanking',
                'customerBanking',
              ]) && (
                <Li>
                  <StyledNavLink to="/banking">Banking</StyledNavLink>
                </Li>
              )}
              {includesAny(permissions, [
                'agentBetting',
                'customerBetting',
              ]) && (
                <Li>
                  <StyledNavLink to="/betting">Betting</StyledNavLink>
                </Li>
              )}
              {includesAny(permissions, ['agentReconcile']) && (
                <Li>
                  <StyledNavLink to="/reconcile">Reconcile</StyledNavLink>
                </Li>
              )}
              {includesAny(permissions, ['mollyWL']) && (
                <Li>
                  <StyledNavLink to="/mollywl">Molly WL</StyledNavLink>
                </Li>
              )}
              {includesAny(permissions, ['admin']) && (
                <Li>
                  <StyledNavLink to="/scrapers">Scrapers</StyledNavLink>
                </Li>
              )}
              {(impersonator || includesAny(permissions, ['admin'])) && (
                <Li
                  style={{
                    opacity:
                      impersonator && !includesAny(permissions, ['admin'])
                        ? 0.5
                        : 1,
                  }}
                >
                  <StyledNavLink to="/admin" onClick={unsudo}>
                    Admin
                  </StyledNavLink>
                </Li>
              )}
            </Ul>
          </LinkContainer>
        </Container>
        <IconContainer>
          <Icon size={1} path={mdiAccount} />
          <UsernameContainer>{username}</UsernameContainer>
          <div style={{ position: 'relative' }}>
            <Tooltip
              content={
                <NotificationsDropdown
                  newNotifications={newNotifications}
                  ackedNotifications={ackedNotifications}
                  getNotifications={getNotifications}
                />
              }
              theme="notifications"
              trigger="click"
              placement="bottom-end"
              interactive={true}
            >
              <IconButton>
                <Icon
                  path={newNotifications.length ? mdiBell : mdiBellOutline}
                  color={
                    newNotifications.length ? theme.primary : theme.foreground
                  }
                />
              </IconButton>
            </Tooltip>
            {!!newNotifications.length && (
              <NotificationIndication>
                {newNotifications.length > 99 ? '99+' : newNotifications.length}
              </NotificationIndication>
            )}
          </div>
          <IconButton onClick={() => setSettingsOpen(!settingsOpen)}>
            <Icon path={mdiCogOutline} />
          </IconButton>
          {settingsOpen && <SettingsDropdown />}
        </IconContainer>
      </Nav>
      <NavSpacer style={{ height: navRef?.current?.offsetHeight || 54 }} />
      {isDangerousDev() && (
        <WarningMessage>
          You are using the real database, changes you make here will affect the
          real data!
        </WarningMessage>
      )}
      {isCloneDb() && (
        <OkMessage>
          You are using the clone database, changes you make here will not
          affect the real data.
        </OkMessage>
      )}
      {!termsState.pending && !termsState.tncs?.terms_accepted && (
        <TermsMessage>
          We have updated our terms and conditions. To view them&nbsp;
          <a
            href="https://totusbet.com/termsandconditions.html"
            target="_blank"
            rel="noreferrer"
          >
            click here.
          </a>
          <Button onClick={acceptTermsAndConditions} variant="primary">
            Accept
          </Button>
        </TermsMessage>
      )}
    </>
  );
}

export default NavBar;
